/**
 * Catálogo de objetos (mobiliario y decoración), diseñado como datos, no como código.
 *
 * Para añadir un nuevo objeto simple NO es necesario tocar los componentes del editor:
 * basta con agregar una entrada aquí. Cada objeto se representa mediante una composición
 * de formas primitivas ligeras (cajas y cilindros) descritas en `parts`, lo que evita
 * depender de modelos GLTF pesados en el MVP. La estructura queda preparada para que,
 * en una fase posterior, `parts` sea reemplazado u complementado por `modelUrl` (glTF).
 */

export type PrimitiveShape = 'box' | 'cylinder'

export interface CatalogPart {
  shape: PrimitiveShape
  /** tamaño en metros: [ancho, alto, profundo] para box; [radio, alto] relevante para cylinder */
  size: [number, number, number]
  /** desplazamiento del centro de la pieza respecto al centro del objeto, en metros */
  offset: [number, number, number]
  color?: string
}

export type FurnitureCategoryGroup = 'mueble' | 'decoracion' | 'estructural'

export interface CatalogItem {
  id: string
  group: FurnitureCategoryGroup
  category: string
  name: string
  defaultWidth: number
  defaultDepth: number
  defaultHeight: number
  defaultColor: string
  parts: CatalogPart[]
  /** ruta de modelo 3D real, opcional — no usado en el MVP */
  modelUrl?: string
}

const box = (
  size: [number, number, number],
  offset: [number, number, number],
  color?: string,
): CatalogPart => ({ shape: 'box', size, offset, color })

const cyl = (
  size: [number, number, number],
  offset: [number, number, number],
  color?: string,
): CatalogPart => ({ shape: 'cylinder', size, offset, color })

export const FURNITURE_CATALOG: CatalogItem[] = [
  // ---- Sala ----
  {
    id: 'sofa-3p',
    group: 'mueble',
    category: 'Sala',
    name: 'Sofá 3 plazas',
    defaultWidth: 2.0,
    defaultDepth: 0.9,
    defaultHeight: 0.8,
    defaultColor: '#8a8578',
    parts: [box([2.0, 0.4, 0.9], [0, 0.2, 0]), box([2.0, 0.4, 0.2], [0, 0.55, -0.35])],
  },
  {
    id: 'sillon',
    group: 'mueble',
    category: 'Sala',
    name: 'Sillón',
    defaultWidth: 0.9,
    defaultDepth: 0.85,
    defaultHeight: 0.8,
    defaultColor: '#8a8578',
    parts: [box([0.9, 0.4, 0.85], [0, 0.2, 0]), box([0.9, 0.4, 0.2], [0, 0.55, -0.325])],
  },
  {
    id: 'mesa-centro',
    group: 'mueble',
    category: 'Sala',
    name: 'Mesa de centro',
    defaultWidth: 1.1,
    defaultDepth: 0.55,
    defaultHeight: 0.4,
    defaultColor: '#b9a888',
    parts: [box([1.1, 0.06, 0.55], [0, 0.37, 0])],
  },
  {
    id: 'mueble-tv',
    group: 'mueble',
    category: 'Sala',
    name: 'Mueble de TV',
    defaultWidth: 1.6,
    defaultDepth: 0.4,
    defaultHeight: 0.5,
    defaultColor: '#5b5348',
    parts: [box([1.6, 0.5, 0.4], [0, 0.25, 0])],
  },
  {
    id: 'estanteria',
    group: 'mueble',
    category: 'Sala',
    name: 'Estantería',
    defaultWidth: 0.9,
    defaultDepth: 0.3,
    defaultHeight: 1.9,
    defaultColor: '#6f6656',
    parts: [box([0.9, 1.9, 0.3], [0, 0.95, 0])],
  },
  // ---- Dormitorio ----
  {
    id: 'cama-2p',
    group: 'mueble',
    category: 'Dormitorio',
    name: 'Cama 2 plazas',
    defaultWidth: 1.6,
    defaultDepth: 2.0,
    defaultHeight: 0.55,
    defaultColor: '#c9c2b4',
    parts: [box([1.6, 0.3, 2.0], [0, 0.15, 0]), box([1.6, 0.25, 0.1], [0, 0.4, -0.95])],
  },
  {
    id: 'mesa-noche',
    group: 'mueble',
    category: 'Dormitorio',
    name: 'Mesa de noche',
    defaultWidth: 0.45,
    defaultDepth: 0.4,
    defaultHeight: 0.5,
    defaultColor: '#a89a80',
    parts: [box([0.45, 0.5, 0.4], [0, 0.25, 0])],
  },
  {
    id: 'armario',
    group: 'mueble',
    category: 'Dormitorio',
    name: 'Armario',
    defaultWidth: 1.2,
    defaultDepth: 0.6,
    defaultHeight: 2.0,
    defaultColor: '#6f6656',
    parts: [box([1.2, 2.0, 0.6], [0, 1.0, 0])],
  },
  {
    id: 'comoda',
    group: 'mueble',
    category: 'Dormitorio',
    name: 'Cómoda',
    defaultWidth: 1.0,
    defaultDepth: 0.45,
    defaultHeight: 0.85,
    defaultColor: '#7a705d',
    parts: [box([1.0, 0.85, 0.45], [0, 0.425, 0])],
  },
  {
    id: 'escritorio',
    group: 'mueble',
    category: 'Dormitorio',
    name: 'Escritorio',
    defaultWidth: 1.2,
    defaultDepth: 0.6,
    defaultHeight: 0.75,
    defaultColor: '#a89a80',
    parts: [box([1.2, 0.06, 0.6], [0, 0.72, 0]), box([0.06, 0.72, 0.06], [-0.55, 0.36, -0.25]), box([0.06, 0.72, 0.06], [0.55, 0.36, -0.25])],
  },
  // ---- Comedor ----
  {
    id: 'mesa-comedor',
    group: 'mueble',
    category: 'Comedor',
    name: 'Mesa de comedor',
    defaultWidth: 1.6,
    defaultDepth: 0.9,
    defaultHeight: 0.75,
    defaultColor: '#a89a80',
    parts: [box([1.6, 0.06, 0.9], [0, 0.72, 0])],
  },
  {
    id: 'silla',
    group: 'mueble',
    category: 'Comedor',
    name: 'Silla',
    defaultWidth: 0.45,
    defaultDepth: 0.45,
    defaultHeight: 0.9,
    defaultColor: '#8a8578',
    parts: [box([0.45, 0.05, 0.45], [0, 0.45, 0]), box([0.45, 0.5, 0.05], [0, 0.7, -0.2])],
  },
  {
    id: 'aparador',
    group: 'mueble',
    category: 'Comedor',
    name: 'Aparador',
    defaultWidth: 1.4,
    defaultDepth: 0.45,
    defaultHeight: 0.9,
    defaultColor: '#6f6656',
    parts: [box([1.4, 0.9, 0.45], [0, 0.45, 0])],
  },
  // ---- Cocina ----
  {
    id: 'encimera',
    group: 'mueble',
    category: 'Cocina',
    name: 'Encimera',
    defaultWidth: 2.0,
    defaultDepth: 0.6,
    defaultHeight: 0.9,
    defaultColor: '#d8d3c6',
    parts: [box([2.0, 0.9, 0.6], [0, 0.45, 0])],
  },
  {
    id: 'cocina',
    group: 'mueble',
    category: 'Cocina',
    name: 'Cocina / estufa',
    defaultWidth: 0.6,
    defaultDepth: 0.6,
    defaultHeight: 0.9,
    defaultColor: '#3d3d3d',
    parts: [box([0.6, 0.9, 0.6], [0, 0.45, 0])],
  },
  {
    id: 'refrigerador',
    group: 'mueble',
    category: 'Cocina',
    name: 'Refrigerador',
    defaultWidth: 0.7,
    defaultDepth: 0.7,
    defaultHeight: 1.8,
    defaultColor: '#c7c9cc',
    parts: [box([0.7, 1.8, 0.7], [0, 0.9, 0])],
  },
  {
    id: 'lavadero',
    group: 'mueble',
    category: 'Cocina',
    name: 'Lavadero',
    defaultWidth: 0.6,
    defaultDepth: 0.55,
    defaultHeight: 0.9,
    defaultColor: '#d8d3c6',
    parts: [box([0.6, 0.9, 0.55], [0, 0.45, 0])],
  },
  {
    id: 'muebles-superiores',
    group: 'mueble',
    category: 'Cocina',
    name: 'Muebles superiores',
    defaultWidth: 1.2,
    defaultDepth: 0.35,
    defaultHeight: 0.7,
    defaultColor: '#e4e0d4',
    parts: [box([1.2, 0.7, 0.35], [0, 1.85, 0])],
  },
  // ---- Oficina ----
  {
    id: 'escritorio-oficina',
    group: 'mueble',
    category: 'Oficina',
    name: 'Escritorio de oficina',
    defaultWidth: 1.4,
    defaultDepth: 0.7,
    defaultHeight: 0.75,
    defaultColor: '#a89a80',
    parts: [box([1.4, 0.06, 0.7], [0, 0.72, 0])],
  },
  {
    id: 'silla-oficina',
    group: 'mueble',
    category: 'Oficina',
    name: 'Silla de oficina',
    defaultWidth: 0.55,
    defaultDepth: 0.55,
    defaultHeight: 1.0,
    defaultColor: '#3d3d3d',
    parts: [box([0.5, 0.06, 0.5], [0, 0.45, 0]), cyl([0.03, 0.45, 0.03], [0, 0.225, 0])],
  },
  {
    id: 'archivador',
    group: 'mueble',
    category: 'Oficina',
    name: 'Archivador',
    defaultWidth: 0.45,
    defaultDepth: 0.6,
    defaultHeight: 1.3,
    defaultColor: '#6f6656',
    parts: [box([0.45, 1.3, 0.6], [0, 0.65, 0])],
  },
  // ---- Baño ----
  {
    id: 'lavabo',
    group: 'mueble',
    category: 'Baño',
    name: 'Lavabo',
    defaultWidth: 0.55,
    defaultDepth: 0.45,
    defaultHeight: 0.85,
    defaultColor: '#eef0ef',
    parts: [box([0.55, 0.1, 0.45], [0, 0.8, 0]), cyl([0.06, 0.75, 0.06], [0, 0.375, 0])],
  },
  {
    id: 'inodoro',
    group: 'mueble',
    category: 'Baño',
    name: 'Inodoro',
    defaultWidth: 0.4,
    defaultDepth: 0.6,
    defaultHeight: 0.4,
    defaultColor: '#eef0ef',
    parts: [box([0.4, 0.4, 0.5], [0, 0.2, 0.05]), box([0.35, 0.35, 0.15], [0, 0.55, -0.2])],
  },
  {
    id: 'ducha',
    group: 'mueble',
    category: 'Baño',
    name: 'Ducha',
    defaultWidth: 0.9,
    defaultDepth: 0.9,
    defaultHeight: 0.1,
    defaultColor: '#dfe3e2',
    parts: [box([0.9, 0.05, 0.9], [0, 0.025, 0])],
  },
  {
    id: 'banera',
    group: 'mueble',
    category: 'Baño',
    name: 'Bañera',
    defaultWidth: 1.7,
    defaultDepth: 0.75,
    defaultHeight: 0.55,
    defaultColor: '#eef0ef',
    parts: [box([1.7, 0.55, 0.75], [0, 0.275, 0])],
  },
  // ---- Decoración ----
  {
    id: 'lampara-pie',
    group: 'decoracion',
    category: 'Iluminación',
    name: 'Lámpara de pie',
    defaultWidth: 0.35,
    defaultDepth: 0.35,
    defaultHeight: 1.6,
    defaultColor: '#c9a15a',
    parts: [cyl([0.02, 1.4, 0.02], [0, 0.7, 0]), cyl([0.18, 0.25, 0.18], [0, 1.45, 0], '#e8d9b5')],
  },
  {
    id: 'planta',
    group: 'decoracion',
    category: 'Plantas',
    name: 'Planta',
    defaultWidth: 0.4,
    defaultDepth: 0.4,
    defaultHeight: 1.1,
    defaultColor: '#4c7a52',
    parts: [cyl([0.18, 0.3, 0.18], [0, 0.15, 0], '#8a6b52'), cyl([0.22, 0.7, 0.22], [0, 0.65, 0])],
  },
  {
    id: 'cuadro',
    group: 'decoracion',
    category: 'Pared',
    name: 'Cuadro',
    defaultWidth: 0.6,
    defaultDepth: 0.03,
    defaultHeight: 0.8,
    defaultColor: '#d7cdb8',
    parts: [box([0.6, 0.8, 0.03], [0, 0, 0])],
  },
  {
    id: 'alfombra',
    group: 'decoracion',
    category: 'Textil',
    name: 'Alfombra',
    defaultWidth: 2.0,
    defaultDepth: 1.4,
    defaultHeight: 0.02,
    defaultColor: '#c98b6b',
    parts: [box([2.0, 0.02, 1.4], [0, 0.01, 0])],
  },
  {
    id: 'espejo',
    group: 'decoracion',
    category: 'Pared',
    name: 'Espejo',
    defaultWidth: 0.5,
    defaultDepth: 0.03,
    defaultHeight: 0.9,
    defaultColor: '#cfd8d6',
    parts: [box([0.5, 0.9, 0.03], [0, 0, 0])],
  },
  {
    id: 'mesa-auxiliar',
    group: 'decoracion',
    category: 'Mobiliario auxiliar',
    name: 'Mesa auxiliar',
    defaultWidth: 0.45,
    defaultDepth: 0.45,
    defaultHeight: 0.5,
    defaultColor: '#b9a888',
    parts: [box([0.45, 0.05, 0.45], [0, 0.475, 0]), cyl([0.03, 0.45, 0.03], [0, 0.225, 0])],
  },
]

export function getCatalogItem(id: string): CatalogItem | undefined {
  return FURNITURE_CATALOG.find((item) => item.id === id)
}

export function catalogByGroup(group: FurnitureCategoryGroup): CatalogItem[] {
  return FURNITURE_CATALOG.filter((item) => item.group === group)
}

export function catalogCategories(group: FurnitureCategoryGroup): string[] {
  return Array.from(new Set(catalogByGroup(group).map((item) => item.category)))
}
